import type { CurrencyCode } from '@bbpos/types';
import type { NotificationType, Notification } from '@/services/notification.service';
import type { Tone } from '@/constants/design-tokens';
import { formatCurrency } from '@/lib/format';

// ─── Type → Icon + Tone mapping (matches admin NotificationCenterPage) ─────

interface TypeMeta {
  tone: Tone;
  iconPath: string;
  label: string;
}

export const NOTIFICATION_TYPE_META: Record<NotificationType, TypeMeta> = {
  LOW_STOCK: {
    tone: 'warning',
    iconPath: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4',
    label: 'Low Stock',
  },
  EXPENSE_APPROVAL: {
    tone: 'danger',
    iconPath: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
    label: 'Expense Approval',
  },
  APPROVAL_RESULT: {
    tone: 'success',
    iconPath: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
    label: 'Approval Result',
  },
  SYNC_CONFLICT: {
    tone: 'danger',
    iconPath: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15',
    label: 'Sync Conflict',
  },
  ORDER_UPDATE: {
    tone: 'violet',
    iconPath: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2',
    label: 'Order Update',
  },
  SHIFT_REMINDER: {
    tone: 'brand',
    iconPath: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
    label: 'Shift Reminder',
  },
  TRIAL_EXPIRY: {
    tone: 'warning',
    iconPath: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
    label: 'Trial Expiry',
  },
  SYSTEM_ANNOUNCEMENT: {
    tone: 'info',
    iconPath: 'M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z',
    label: 'Announcement',
  },
  REPORT_READY: {
    tone: 'success',
    iconPath: 'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
    label: 'Report Ready',
  },
  BACKUP_COMPLETE: {
    tone: 'teal',
    iconPath: 'M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4',
    label: 'Backup Complete',
  },
  CREDIT_LIMIT: {
    tone: 'danger',
    iconPath: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z',
    label: 'Credit Limit',
  },
  // G2.9: new types added to the shared NotificationType union. These
  // reuse existing icon glyphs from the set above so visual parity with
  // related events is preserved.
  APPROVAL_REQUEST: {
    tone: 'warning',
    iconPath: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
    label: 'Approval Request',
  },
  ORDER_NEW: {
    tone: 'violet',
    iconPath: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2',
    label: 'New Order',
  },
  TABLE_STATUS: {
    tone: 'brand',
    iconPath: 'M4 6h16M4 10h16M4 14h16M4 18h16',
    label: 'Table Status',
  },
  PLATFORM_SUSPENSION: {
    tone: 'danger',
    iconPath: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
    label: 'Platform Suspension',
  },
  TENANT_CANCELLED: {
    tone: 'danger',
    iconPath: 'M6 18L18 6M6 6l12 12',
    label: 'Tenant Cancelled',
  },
  BILLING_FAILED: {
    tone: 'danger',
    iconPath: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z',
    label: 'Billing Failed',
  },
  EMAIL_DELIVERY_FAILED: {
    tone: 'danger',
    iconPath: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
    label: 'Email Delivery Failed',
  },
  EMAIL_DELIVERY_RESTORED: {
    tone: 'success',
    iconPath: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
    label: 'Email Delivery Restored',
  },
  QUOTE_APPROVED: {
    tone: 'success',
    iconPath: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
    label: 'Quote Approved',
  },
  QUOTE_REJECTED: {
    tone: 'danger',
    iconPath: 'M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z',
    label: 'Quote Declined',
  },
  QUOTE_CHANGES_REQUESTED: {
    tone: 'violet',
    iconPath: 'M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z',
    label: 'Quote Changes Requested',
  },
  // PU4-007 — a vendor bill due in 3 days / on its first overdue day.
  VENDOR_BILL_DUE_SOON: {
    tone: 'warning',
    iconPath: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
    label: 'Vendor Bill Due Soon',
  },
  VENDOR_BILL_OVERDUE: {
    tone: 'danger',
    iconPath: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
    label: 'Vendor Bill Overdue',
  },
  // L04 — a manager's purchase order or PO change waits for the owner. The POS
  // has no PO screen, so a click opens the alert's detail view.
  PO_APPROVAL_REQUEST: {
    tone: 'violet',
    iconPath: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
    label: 'PO Approval',
  },
};

// ─── Click target ───────────────────────────────────────────────────────────

const QUOTE_TYPES: ReadonlySet<string> = new Set(['QUOTE_APPROVED', 'QUOTE_REJECTED', 'QUOTE_CHANGES_REQUESTED']);
/** Types whose `bill_id` is the record to open. A parked-bill alert is not one: resume it from billing. */
const BILL_TYPES: ReadonlySet<string> = new Set([
  'APPROVAL_REQUEST',
  'APPROVAL_RESULT',
  'SALE_COMPLETE',
  'NEGATIVE_FEEDBACK',
]);

function idIn(metadata: Record<string, unknown> | null | undefined, key: string): string | null {
  const v = metadata?.[key];
  return typeof v === 'string' && v ? v : null;
}

/**
 * The POS screen a notification opens, if it has one. The API's `action_url`
 * is an admin path, so the POS maps the record's id to its own route (T11
 * quotes, T24 everything else) and only follows an action_url that is already
 * a POS route. No POS screen → null, and the click opens the detail view.
 */
export function notificationTarget(n: Pick<Notification, 'type' | 'action_url' | 'metadata'>): string | null {
  const quotationId = idIn(n.metadata, 'quotation_id');
  if (QUOTE_TYPES.has(n.type) && quotationId) return `/pos/quotations/${quotationId}`;
  const productId = idIn(n.metadata, 'product_id');
  if (n.type === 'LOW_STOCK' && productId) return `/pos/products/${productId}`;
  const siteOrderId = idIn(n.metadata, 'site_order_id');
  if (siteOrderId) return `/pos/online-orders/${siteOrderId}`;
  const billId = idIn(n.metadata, 'bill_id');
  if (BILL_TYPES.has(n.type) && billId) return `/pos/bills/${billId}`;
  return typeof n.action_url === 'string' && n.action_url.startsWith('/pos/') ? n.action_url : null;
}

/** Where a click goes (T24): the record's POS screen, else the notification's detail view. */
export function notificationHref(n: Pick<Notification, 'id' | 'type' | 'action_url' | 'metadata'>): string {
  return notificationTarget(n) ?? `/pos/notifications/${n.id}`;
}

/** A label for any type, including ones outside NOTIFICATION_TYPE_META ('SALES_ANOMALY'). */
export function notificationTypeLabel(type: string): string {
  return NOTIFICATION_TYPE_META[type as NotificationType]?.label ?? humanize(type);
}

// ─── Readable data fields (detail view, T24) ────────────────────────────────

/** Links and ids are not for reading: the link is the "Open" button. */
const HIDDEN_KEYS = new Set(['action_url', 'path']);
/** Minor-unit amounts the producers send (return approval, sale complete, sales anomaly). */
const MONEY_KEYS = new Set(['amount', 'grand_total', 'weekday_average']);
const PERCENT_KEYS = new Set(['change_pct', 'threshold_pct', 'percent']);
const ISO_DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
/**
 * Keys whose values are codes ('COMPLETED', 'STORE_CREDIT', 'return_approval',
 * 'approved') and read as words. Only these: a name, a number or free text
 * ('MAGGI', 'RINV-1', a comment) is shown as written.
 */
const CODE_KEYS = new Set(['result', 'status', 'scope', 'metric', 'rule']);
const CODE_KEY_SUFFIX = /_(?:type|mode|method|status)$/;

export function humanize(key: string): string {
  const words = key.replace(/_/g, ' ').trim().toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export interface ReadableField {
  key: string;
  label: string;
  value: string;
}

/**
 * The notification's `data` as label / value rows. Ids, links, empty values
 * and nested objects are left out; money is shown in the tenant's currency and
 * timestamps on the store's clock. Mirrors admin `notification-links.ts`.
 */
export function readableDataFields(
  data: Record<string, unknown> | null | undefined,
  opts: { currency: CurrencyCode; timeZone: string },
): ReadableField[] {
  if (!data) return [];
  const rows: ReadableField[] = [];
  for (const [key, raw] of Object.entries(data)) {
    if (HIDDEN_KEYS.has(key) || key === 'id' || key.endsWith('_id')) continue;
    const value = formatDataValue(key, raw, data, opts);
    if (value !== null) rows.push({ key, label: humanize(key), value });
  }
  return rows;
}

function formatDataValue(
  key: string,
  raw: unknown,
  data: Record<string, unknown>,
  opts: { currency: CurrencyCode; timeZone: string },
): string | null {
  if (raw === null || raw === undefined || raw === '') return null;
  if (typeof raw === 'boolean') return raw ? 'Yes' : 'No';
  if (Array.isArray(raw)) {
    const items = raw.filter((v) => typeof v === 'string' || typeof v === 'number');
    return items.length > 0 ? items.join(', ') : null;
  }
  if (typeof raw === 'object') return null;

  const numeric = typeof raw === 'number' ? raw : /^-?\d+(\.\d+)?$/.test(String(raw)) ? Number(raw) : null;
  if (numeric !== null) {
    if (MONEY_KEYS.has(key)) return formatCurrency(numeric, opts.currency);
    if (PERCENT_KEYS.has(key)) return `${numeric}%`;
    // A discount request carries basis points for a percentage, minor units for a flat amount.
    if (key === 'discount_value') {
      return data.discount_type === 'PERCENTAGE'
        ? `${numeric / 100}%`
        : formatCurrency(numeric, opts.currency);
    }
    return numeric.toLocaleString('en-IN');
  }

  const text = String(raw);
  if (ISO_DATE_TIME.test(text)) {
    const d = new Date(text);
    if (!Number.isNaN(d.getTime())) return formatDateTime(d, opts.timeZone);
  }
  return CODE_KEYS.has(key) || CODE_KEY_SUFFIX.test(key) ? humanize(text) : text;
}

export function formatDateTime(d: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('en-IN', { timeZone, dateStyle: 'medium', timeStyle: 'short' }).format(d);
}

// ─── Tone → CSS classes ─────────────────────────────────────────────────────

export function toneIconClass(tone: Tone): string {
  const map: Record<Tone, string> = {
    success: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
    warning: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
    danger: 'bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400',
    info: 'bg-sky-50 dark:bg-sky-900/30 text-sky-600 dark:text-sky-400',
    teal: 'bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400',
    violet: 'bg-violet-50 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400',
    brand: 'bg-brand-50 dark:bg-brand-900/30 text-brand-600 dark:text-brand-400',
  };
  return map[tone];
}

// ─── Time helpers ───────────────────────────────────────────────────────────

export function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  try {
    return new Date(iso).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

// ─── Date grouping ──────────────────────────────────────────────────────────

export type DateGroup = 'Today' | 'Yesterday' | 'This Week' | 'Older';

export function getDateGroup(iso: string): DateGroup {
  const now = new Date();
  const date = new Date(iso);
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterdayStart = new Date(todayStart.getTime() - 86400000);
  const weekStart = new Date(todayStart.getTime() - 6 * 86400000);

  if (date >= todayStart) return 'Today';
  if (date >= yesterdayStart) return 'Yesterday';
  if (date >= weekStart) return 'This Week';
  return 'Older';
}

export function groupNotifications(
  notifications: Notification[],
): { group: DateGroup; items: Notification[] }[] {
  const groups: Record<DateGroup, Notification[]> = {
    Today: [],
    Yesterday: [],
    'This Week': [],
    Older: [],
  };
  notifications.forEach((n) => groups[getDateGroup(n.created_at)].push(n));
  const order: DateGroup[] = ['Today', 'Yesterday', 'This Week', 'Older'];
  return order
    .filter((g) => groups[g].length > 0)
    .map((g) => ({ group: g, items: groups[g] }));
}
